<template>
  <div
      :class="[
          'buttonsContainerClass',
          positionClass, // Dynamically bind position class
      ]"
      :style="{
        backgroundColor: buttonColor,
        borderColor: lightenDarkenColor(buttonColor, -50),
        borderRadius: containerBorderRadius, // Use computed property here
        maxWidth: '100px'
      }"
  >
    <div
        v-for="(link, index) in links"
        :key="index"
        data-tw-class="cursor-pointer leading-6 text-decoration-line[none] flex justify-center items-center px-2"
        class="button"
        :class="buttonClasses(index)"
        :style="{
          backgroundColor: index === 0 ? buttonColor : '',
          color: textColor,
          height: buttonHeight(index),
          width: buttonWidth(),
          borderColor: index !== 0 ? lightenDarkenColor(buttonColor, -20) : 'transparent',
          ':hover': { backgroundColor: lightenDarkenColor(buttonColor, -10) }
        }"
        @click="handleClick(link.action)"
    >
      <a
          :href="link.type === 'url' ? link.action : ''"
          class="linkClass"
          data-tw-class="no-underline flex items-center space-x-2"
      >
        <i
            v-if="link.icon"
            :class="link.icon"
        />
        <p
            class="textClass"
            data-tw-class="uppercase text-xs md:text-sm lg:text-lg"
        >
          {{ link.text }}
        </p>
      </a>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'SidebarButtons',
  props: ['widgetId'],
  data() {
    return {
      links: [], // This will hold the links fetched from the API
      buttonColor: '', // Hex color for button background
      textColor: '', // Hex color for text
      position: 'right_middle' // Default position
    };
  },
  mounted() {
    this.fetchLinks();
  },
  computed: {
    positionClass() {
      switch (this.position) {
        case 'right_top':
          return 'position-right-top';
        case 'right_bottom':
          return 'position-right-bottom';
        case 'left_middle':
          return 'position-left-middle';
        case 'left_top':
          return 'position-left-top';
        case 'left_bottom':
          return 'position-left-bottom';
        case 'right_middle':
        default:
          return 'position-right-middle';
      }
    },
    containerBorderRadius() {
      // Determine border-radius based on the position
      if (this.position.startsWith('left')) {
        return '0 10px 10px 0'; // Round right corners
      } else {
        return '10px 0 0 10px'; // Round left corners (for right positions)
      }
    }
  },
  methods: {
    async fetchLinks() {
      try {
        let rawApiResponse = await axios.get(process.env.VUE_APP_API_URL + '/api/widget/' + this.widgetId);
        let response = rawApiResponse.data;

        this.buttonColor = response.button_colour;
        this.textColor = response.text_colour;
        this.links = response.links;

        // Set the position based on API response, default to 'right_middle' if not provided
        this.position = response.position || 'right_middle';
      } catch (error) {
        console.error("Error fetching widget data:", error);

        this.buttonColor = '#007bff'; // Default blue
        this.textColor = '#ffffff';
        this.links = [
          {type: 'url', action: '#', text: 'Error', icon: 'fas fa-exclamation-triangle'}
        ];
        this.position = 'right_middle';
      }
    },
    handleClick(action) {
      if (typeof action === 'string' && action.startsWith('javascript:')) {
        eval(action.substring(11)); // Executes the JavaScript code
      }
    },
    lightenDarkenColor(col, amt) {
      let usePound = false;
      if (col[0] === "#") {
        col = col.slice(1);
        usePound = true;
      }
      let num = parseInt(col, 16);
      let r = (num >> 16) + amt;
      let b = ((num >> 8) & 0x00FF) + amt;
      let g = (num & 0x0000FF) + amt;
      return (usePound ? "#" : "") + ((g | (b << 8) | (r << 16)).toString(16).padStart(6, '0'));
    },
    buttonClasses(index) {
      // Adjust button individual corner rounding based on overall position
      const baseClasses = [
        'md:w-22 md:h-[190px]', // Medium screens
        'lg:w-28 lg:h-[198px]', // Large screens
        {
          'border-t': index !== 0
        }
      ];

      // Add conditional rounding for the first and last button based on widget position
      if (this.position.startsWith('left')) {
        if (index === 0) {
          baseClasses.push('rounded-tr-[25px] rounded-br-[25px]');
        }
        if (index === this.links.length - 1) {
          baseClasses.push('rounded-bl-[25px] rounded-tl-[25px]');
        }
      } else { // Right positions
        if (index === 0) {
          baseClasses.push('rounded-tl-[25px] rounded-bl-[25px]');
        }
        if (index === this.links.length - 1) {
          baseClasses.push('rounded-br-[25px] rounded-tr-[25px]');
        }
      }
      return baseClasses;
    },
    buttonHeight(index) {
      // Return a dynamic height based on the index
      if (index === 0) return '148px'; // First button
      if (index === this.links.length - 1) return '148px'; // Last button
      return '120px'; // Middle buttons
    },
    buttonWidth() {
      return '100%'; // Full width of the parent container
    }
  }
};
</script>

<style scoped>
.no-underline {
  text-decoration: none;
}

.button {
  cursor: pointer; /* cursor-pointer */
  line-height: 1.5rem; /* leading-6 */
  text-decoration-line: none; /* text-decoration-line[none] */
  display: flex; /* flex */
  justify-content: center; /* justify-center */
  align-items: center; /* items-center */
  padding-left: 0.5rem; /* px-2 */
  padding-right: 0.5rem; /* px-2 */
}

/* Base styles for the container */
.buttonsContainerClass {
  display: flex; /* flex */
  flex-direction: column; /* flex-col */
  position: fixed; /* fixed */
  z-index: 50; /* z-50 */
  /* Default position (right middle) - these will be overridden by specific position classes */
  /* Remove explicit border-radius here as it's now dynamic in the template style binding */
  /* right: 0px; top: 50%; transform: translateY(-50%); */
}

.linkClass {
  text-decoration: none; /* no-underline */
  display: flex; /* flex */
  align-items: center; /* items-center */
  gap: 0.5rem; /* space-x-2 */
}

.textClass {
  text-transform: uppercase; /* uppercase */
  font-size: 0.75rem; /* text-xs */
  line-height: 1rem; /* text-xs */
}

/* --- Position Classes --- */

/* Right Top */
.position-right-top {
  right: 0px;
  top: 20px; /* Adjust as needed */
  transform: none; /* Remove translateY if previously applied */
  /* border-radius handled by computed property */
}

/* Right Middle (Default) */
.position-right-middle {
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  /* border-radius handled by computed property */
}

/* Right Bottom */
.position-right-bottom {
  right: 0px;
  bottom: 20px; /* Adjust as needed */
  top: auto; /* Ensure top is not set */
  transform: none;
  /* border-radius handled by computed property */
}

/* Left Top */
.position-left-top {
  left: 0px;
  top: 20px; /* Adjust as needed */
  right: auto; /* Ensure right is not set */
  transform: none;
  /* border-radius handled by computed property */
}

/* Left Middle */
.position-left-middle {
  left: 0px;
  top: 50%;
  right: auto;
  transform: translateY(-50%);
  /* border-radius handled by computed property */
}

/* Left Bottom */
.position-left-bottom {
  left: 0px;
  bottom: 20px; /* Adjust as needed */
  top: auto;
  right: auto;
  transform: none;
  /* border-radius handled by computed property */
}


/* Media Queries for responsiveness */
@media (min-width: 768px) {
  /* No explicit changes needed for top/bottom positions, as they are absolute from edge */
  .position-right-middle.buttonsContainerClass {
    top: 250.5px; /* md:top-[250.5px] */
    transform: none; /* Reset transform for fixed top */
  }

  .position-left-middle.buttonsContainerClass {
    top: 250.5px; /* md:top-[250.5px] */
    transform: none; /* Reset transform for fixed top */
  }


  .textClass {
    font-size: 0.875rem; /* md:text-sm */
    line-height: 1.25rem; /* md:text-sm */
  }
}

@media (min-width: 1024px) {
  .position-right-middle.buttonsContainerClass {
    top: 350.5px; /* lg:top-[350.5px] */
  }

  .position-left-middle.buttonsContainerClass {
    top: 350.5px; /* lg:top-[350.5px] */
  }

  .textClass {
    font-size: 0.8rem; /* lg:text-lg */
    line-height: 1.25rem; /* lg:text-lg */
  }
}
</style>